import React, { useMemo, useState } from "react";
import { StandardPageWrapper } from "../../../../components/layout/StandardPageWrapper";
import { Routes } from "../../../../root/routes";
import { RoPAPageTabs } from "./RoPAPageHome";
import { usePrimaryOrganizationId } from "../../../../hooks";
import {
  useRoPADownloader,
  useRoPAFunctions,
  useRoPAHistory,
} from "../../../../../common/hooks/useRoPA";

import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Breadcrumbs } from "@truevault/aquilo";
``;
import { RoPASummaryDto } from "../../../../../common/service/server/dto/RoPADto";
import { Fmt } from "../../../../formatters";

import * as MuiIcon from "@mui/icons-material";
import { UUIDString } from "../../../../../common/service/server/Types";
import { useSnackBar } from "@truevault/aquilo";

type RoPAHistoryRowProps = {
  orgId: UUIDString;
  ropa: RoPASummaryDto;
  refreshData: () => Promise<void>;
};

const RoPAHistoryRow = ({ orgId, ropa, refreshData }: RoPAHistoryRowProps) => {
  const ropaDownloader = useRoPADownloader(orgId);
  const ropaFunctions = useRoPAFunctions(orgId);
  const snackbar = useSnackBar();
  const [pendingDeletion, setPendingDeletion] = useState<boolean>(false);

  return (
    <TableRow>
      <TableCell>
        <Typography variant={"subtitle2"}>Record of Processing Activities</Typography>
      </TableCell>
      <TableCell>{Fmt.Date.fmtDate(ropa.lastUpdated)}</TableCell>
      <TableCell>
        {pendingDeletion ? (
          <div style={{ textAlign: "right" }}>
            <small>Deleting...</small>
          </div>
        ) : (
          <div className="tw-boundary" style={{ textAlign: "right" }}>
            <Button
              onClick={() => {
                let filename = `RoPA ${Fmt.Date.fmtDate(ropa.lastUpdated)}`;
                void ropaDownloader.openRoPA(ropa.id, filename);
              }}
              aria-label={`View RoPA from ${Fmt.Date.fmtDate(ropa.lastUpdated)}`}
            >
              <MuiIcon.VisibilityOutlined />
            </Button>
            <Button
              onClick={() => {
                let filename = `RoPA ${Fmt.Date.fmtDate(ropa.lastUpdated)}`;
                void ropaDownloader.downloadRoPA(ropa.id, filename);
              }}
              aria-label={`Download RoPA from ${Fmt.Date.fmtDate(ropa.lastUpdated)}`}
            >
              <MuiIcon.DownloadOutlined />
            </Button>
            <Button
              onClick={() => {
                setPendingDeletion(true);
                let success = ropaFunctions.deleteRoPA(ropa.id);
                if (!success) {
                  snackbar(`Couldn't delete RoPA - try again soon.`, {
                    variant: "error",
                  });
                  setPendingDeletion(false);
                } else {
                  setTimeout(() => {
                    void refreshData();
                  }, 1000);
                }
              }}
              aria-label={`Discard RoPA from ${Fmt.Date.fmtDate(ropa.lastUpdated)}`}
            >
              <MuiIcon.DeleteOutlineRounded />
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
};

export const RoPAPageHistory = () => {
  const orgId = usePrimaryOrganizationId();

  const [ropaHistory, ropaHistoryRequest, ropaHistoryRefresh] = useRoPAHistory(orgId);
  const ropaHistorySorted = useMemo(() => {
    if (!ropaHistory) {
      return [];
    }
    return ropaHistory.sort(
      (a, b) => new Date(b?.lastUpdated || 0).getTime() - new Date(a?.lastUpdated || 0).getTime(),
    );
  }, [ropaHistory]);

  return (
    <StandardPageWrapper requests={[ropaHistoryRequest]}>
      <div className="tw-boundary">
        <Breadcrumbs
          title="Record of Processing Activities (RoPA)"
          source="Privacy Docs"
          sourceRoute={Routes.documents.root}
        ></Breadcrumbs>
      </div>
      <RoPAPageTabs currentTab="history"></RoPAPageTabs>

      {ropaHistorySorted?.length > 0 ? (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Version Date</TableCell>
              <TableCell style={{ textAlign: "right" }}>View / Download / Delete</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ropaHistorySorted.map((ropa) => (
              <RoPAHistoryRow
                key={ropa.id}
                orgId={orgId}
                ropa={ropa}
                refreshData={ropaHistoryRefresh}
              />
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="empty-page">
          <img src="/assets/images/stay_compliant/empty-page.svg" alt="add" />
          <p>No Previous Versions</p>
        </div>
      )}
    </StandardPageWrapper>
  );
};
